import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ModuleSearchResult, searchApi } from '@/lib/api/search'
import { createLogger } from '@/lib/logger'

const logger = createLogger('hooks/useModuleSearch')

interface UseModuleSearchOptions {
  limit?: number
  minSearchLength?: number
  debounceMs?: number
  showDueSoonInEmptyState?: boolean
  loadRecentOnMount?: boolean // Load recent items when component mounts
  allowedTypes?: string[] // Filter by module types
  collectionId?: string // Filter custom collection results by collection ID
  parentType?: ModuleSearchResult['type'] // Optional scoped search root type
  parentId?: string // Optional scoped search root ID
  excludePrivateNotes?: boolean
}

export function useModuleSearch(options: UseModuleSearchOptions = {}) {
  const {
    limit = 50,
    minSearchLength = 2,
    debounceMs = 300,
    showDueSoonInEmptyState = false,
    loadRecentOnMount = false,
    allowedTypes,
    collectionId,
    parentType,
    parentId,
    excludePrivateNotes = false,
  } = options

  // Stabilize allowedTypes to prevent infinite loops when array reference changes
  const allowedTypesKey = useMemo(
    () => allowedTypes?.join(',') || '',
    [allowedTypes],
  )
  const scopeKey = useMemo(
    () => `${parentType || ''}:${parentId || ''}`,
    [parentType, parentId],
  )

  // Use a ref to store the actual allowedTypes array for API calls
  const allowedTypesRef = useRef(allowedTypes)
  allowedTypesRef.current = allowedTypes

  const [query, setQuery] = useState('')
  const [results, setResults] = useState<ModuleSearchResult[]>([])
  const [previousResults, setPreviousResults] = useState<ModuleSearchResult[]>(
    [],
  ) // Keep previous results during loading
  const [dueSoonTasks, setDueSoonTasks] = useState<ModuleSearchResult[]>([])
  const [recentItems, setRecentItems] = useState<ModuleSearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingDueSoon, setIsLoadingDueSoon] = useState(false)
  const [isLoadingRecent, setIsLoadingRecent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastQuery, setLastQuery] = useState('') // Track the query that produced current results

  // Use ref to access results without causing callback re-creation
  const resultsRef = useRef(results)
  resultsRef.current = results

  const search = useCallback(
    async (searchQuery: string) => {
      // For initial load of recent items, skip if empty query and not loading recent
      if (
        !searchQuery ||
        (searchQuery.length < minSearchLength && !loadRecentOnMount)
      ) {
        setResults([])
        setPreviousResults([])
        setLastQuery('')
        setIsLoading(false)
        setError(null)
        return
      }

      // Preserve current results while loading new ones (prevents flickering)
      if (resultsRef.current.length > 0) {
        setPreviousResults(resultsRef.current)
      }

      setIsLoading(true)
      setError(null)

      try {
        const searchResults = await searchApi.searchModules({
          q: searchQuery,
          limit,
          types: allowedTypesRef.current, // Use ref to get current value without triggering re-creation
          collectionId,
          parentType,
          parentId,
          excludePrivateNotes,
        })
        setResults(searchResults)
        setPreviousResults(searchResults) // Update previous results on success
        setLastQuery(searchQuery)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed')
        // On error, keep previous results instead of clearing
      } finally {
        setIsLoading(false)
      }
    },
    [
      limit,
      minSearchLength,
      allowedTypesKey,
      collectionId,
      loadRecentOnMount,
      scopeKey,
      excludePrivateNotes,
    ],
  ) // Use stable keys instead of array/object refs

  // Fetch due soon tasks for empty state
  const fetchDueSoonTasks = useCallback(async () => {
    if (!showDueSoonInEmptyState) return

    setIsLoadingDueSoon(true)
    try {
      const tasks = await searchApi.getTasksDueSoon(10)
      setDueSoonTasks(tasks)
    } catch (err) {
      logger.warn('Failed to fetch due soon tasks:', err)
      setDueSoonTasks([])
    } finally {
      setIsLoadingDueSoon(false)
    }
  }, [showDueSoonInEmptyState])

  // Fetch recent/top items for empty state
  const fetchRecentItems = useCallback(async () => {
    if (!loadRecentOnMount) return

    setIsLoadingRecent(true)
    try {
      const items = await searchApi.getRecentItems({
        limit: 10,
        types: allowedTypesRef.current,
        collectionId,
        parentType,
        parentId,
        excludePrivateNotes,
      })
      setRecentItems(items)
    } catch (err) {
      logger.warn('Failed to fetch recent items:', err)
      setRecentItems([])
    } finally {
      setIsLoadingRecent(false)
    }
  }, [
    loadRecentOnMount,
    allowedTypesKey,
    collectionId,
    scopeKey,
    excludePrivateNotes,
  ]) // Use stable keys instead of array/object refs

  // Auto-search when query changes with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      search(query)
    }, debounceMs)

    return () => clearTimeout(timeoutId)
  }, [query, search, debounceMs])

  // Fetch due soon tasks on mount
  useEffect(() => {
    fetchDueSoonTasks()
  }, [fetchDueSoonTasks])

  // Fetch recent items on mount
  useEffect(() => {
    fetchRecentItems()
  }, [fetchRecentItems])

  // Determine which results to display (use previous results while loading to prevent flickering)
  const displayResults = useMemo(() => {
    // If loading and we have previous results, show them to prevent flickering
    if (isLoading && previousResults.length > 0) {
      return previousResults
    }
    return results
  }, [isLoading, previousResults, results])

  // Group results by type (include due soon tasks or recent items when no search query)
  const groupedResults = useMemo(() => {
    let searchResults = displayResults

    // If no search results and no query, show either due soon tasks or recent items
    if (displayResults.length === 0 && query.length === 0) {
      if (showDueSoonInEmptyState && dueSoonTasks.length > 0) {
        searchResults = dueSoonTasks
      } else if (loadRecentOnMount && recentItems.length > 0) {
        searchResults = recentItems
      }
    }

    const groups = {
      ENTITY: searchResults.filter(r => r.type === 'ENTITY'),
      PROJECT: searchResults.filter(r => r.type === 'PROJECT'),
      TASK_GROUP: searchResults.filter(r => r.type === 'TASK_GROUP'),
      TASK: searchResults.filter(r => r.type === 'TASK'),
      TICKET: searchResults.filter(r => r.type === 'TICKET'),
      DEAL: searchResults.filter(r => r.type === 'DEAL'),
      CONTACT: searchResults.filter(r => r.type === 'CONTACT'),
      EVENT: searchResults.filter(r => r.type === 'EVENT'),
      CONTRACT: searchResults.filter(r => r.type === 'CONTRACT'),
      INVOICE: searchResults.filter(r => r.type === 'INVOICE'),
      INVENTORY: searchResults.filter(r => r.type === 'INVENTORY'),
      SUPPLIER: searchResults.filter(r => r.type === 'SUPPLIER'),
      NOTE: searchResults.filter(r => r.type === 'NOTE'),
      DOCUMENT: searchResults.filter(r => r.type === 'DOCUMENT'),
      CUSTOM_COLLECTION: searchResults.filter(
        r => r.type === 'CUSTOM_COLLECTION',
      ),
      ORGANIZATION: searchResults.filter(r => r.type === 'ORGANIZATION'),
    }
    return groups
  }, [
    displayResults,
    dueSoonTasks,
    recentItems,
    query,
    showDueSoonInEmptyState,
    loadRecentOnMount,
  ])

  const clearSearch = useCallback(() => {
    setQuery('')
    setResults([])
    setPreviousResults([])
    setLastQuery('')
    setError(null)
  }, [])

  // Determine if we're showing empty state with due soon tasks or recent items
  const isShowingDueSoon =
    query.length === 0 && dueSoonTasks.length > 0 && showDueSoonInEmptyState
  const isShowingRecent =
    query.length === 0 &&
    recentItems.length > 0 &&
    loadRecentOnMount &&
    !isShowingDueSoon

  // Compute the final results to return
  const finalResults = useMemo(() => {
    if (displayResults.length > 0) return displayResults
    if (isShowingDueSoon) return dueSoonTasks
    if (isShowingRecent) return recentItems
    return []
  }, [
    displayResults,
    isShowingDueSoon,
    isShowingRecent,
    dueSoonTasks,
    recentItems,
  ])

  return {
    query,
    setQuery,
    results: finalResults,
    groupedResults,
    isLoading,
    isLoadingInitial:
      query.length === 0 && (isLoadingDueSoon || isLoadingRecent), // Separate initial loading state
    isShowingDueSoon,
    isShowingRecent,
    error,
    lastQuery, // Expose lastQuery so components know what results correspond to
    search: (searchQuery: string) => {
      setQuery(searchQuery)
    },
    clearSearch,
    refreshDueSoon: fetchDueSoonTasks,
    refreshRecent: fetchRecentItems,
  }
}
